import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import API_CONFIG from './config/api';
import Promissorias from './Promissorias';
import Vencidos from './Vencidos';

const URL = API_CONFIG.API_URL;

const normalizarTexto = (valor) => (valor || '')
  .toString()
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '');

const DetalhesQuitacao = ({ conta }) => conta.quitacao?.negociacao_id ? (
  <div style={{ fontSize: '0.78rem', fontWeight: 400, color: '#475569', marginTop: '5px', lineHeight: 1.5 }}>
    <div>Veículo: {conta.quitacao.veiculo || 'Não informado'} · Placa: {conta.quitacao.placa || 'Não informada'}</div>
    <div>Vendedor: {conta.quitacao.vendedor || 'Não informado'}</div>
    <div>Instituição: {conta.quitacao.instituicao || 'Não informada'}</div>
  </div>
) : null;

// ─── Card de ferramenta (mesmo padrão do menu Análise) ───────────────────────
const ToolCard = ({ label, icon, onClick }) => (
  <button
    onClick={onClick}
    style={{
      width: '150px', height: '150px', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: '14px',
      background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: '14px',
      cursor: 'pointer', transition: 'box-shadow 0.15s, border-color 0.15s',
      padding: '20px', color: '#475569'
    }}
    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; e.currentTarget.style.borderColor = '#94a3b8'; }}
    onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
  >
    {icon}
    <span style={{ fontSize: '0.88rem', fontWeight: '600', color: '#334155', textAlign: 'center' }}>{label}</span>
  </button>
);

// ─── Ícone contas a pagar ────────────────────────────────────────────────────
const IconeContasPagar = () => (
  <svg width="38" height="38" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="6" width="18" height="12" rx="3"/>
    <path d="M7 6V4a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v2"/>
    <path d="M3 11h18"/>
    <path d="M16 15h3"/>
  </svg>
);

const IconeResumo = () => (
  <svg width="38" height="38" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19V9"/><path d="M10 19V5"/><path d="M16 19v-7"/><path d="M22 19V3"/>
    <path d="M2 19h21"/>
  </svg>
);

const IconePromissorias = () => (
  <svg width="38" height="38" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="3" width="16" height="18" rx="2" />
    <path d="M8 7h8M8 11h8M8 15h4M8 18h8" />
  </svg>
);

const IconeVencidos = () => <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4m0 4h.01"/><path d="M10.3 3.6 2.5 17a2 2 0 0 0 1.7 3h15.6a2 2 0 0 0 1.7-3L13.7 3.6a2 2 0 0 0-3.4 0Z"/></svg>;

const hojeLocalISO = () => {
  const hoje = new Date();
  const tzOffset = hoje.getTimezoneOffset() * 60000;
  return new Date(hoje.getTime() - tzOffset).toISOString().split('T')[0];
};
const normalizarDataParaLocal = (valor) => {
  if (!valor) return null;
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return null;
  return {
    ano: data.getUTCFullYear(),
    mes: data.getUTCMonth() + 1,
    dia: data.getUTCDate()
  };
};
const formatarDataLocal = (valor) => {
  const d = normalizarDataParaLocal(valor);
  if (!d) return '-';
  return `${String(d.dia).padStart(2, '0')}/${String(d.mes).padStart(2, '0')}/${d.ano}`;
};
const dataParaInputLocal = (valor) => {
  const d = normalizarDataParaLocal(valor);
  if (!d) return hojeLocalISO();
  return `${d.ano}-${String(d.mes).padStart(2, '0')}-${String(d.dia).padStart(2, '0')}`;
};

const calcularDiasEmAtraso = (valor) => {
  const data = normalizarDataParaLocal(valor);
  if (!data) return 0;
  const vencimento = new Date(data.ano, data.mes - 1, data.dia);
  const hoje = new Date();
  const hojeLocal = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
  return Math.max(0, Math.floor((hojeLocal - vencimento) / 86400000));
};

const calcularResumoLista = (contas) => contas.reduce((resumo, conta) => {
  const valor = Number(conta.valor || 0);
  const valorPago = Number(conta.valor_pago || 0);
  const saldo = valor - valorPago;
  resumo.total_geral += valor;
  resumo.total_pago += valorPago;
  if (conta.status === 'atrasado') {
    resumo.total_atrasado += saldo;
    resumo.total_pendente += saldo;
  } else if (conta.status !== 'pago') {
    resumo.total_pendente += saldo;
  }
  return resumo;
}, { total_geral: 0, total_pago: 0, total_pendente: 0, total_atrasado: 0, total_contas: contas.length });

export default function ContasPagar({ user, lojaContexto, onToggleSidebar, initialOpen = false, onAbrirNegociacao }) {
  const d = new Date();
  const [mesAno, setMesAno] = useState(() => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  const [opcoesMes, setOpcoesMes] = useState([]);
  const [categorias, setCategorias] = useState(['Aluguel', 'Água/Luz', 'Fornecedores', 'Impostos', 'Salários', 'Outros']);
  const [fornecedoresRapidos, setFornecedoresRapidos] = useState([]);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 768 : false));
  const m = parseInt(mesAno.split('-')[1], 10);
  const a = parseInt(mesAno.split('-')[0], 10);
  const [list, setList] = useState([]);
  const [res, setRes] = useState({ total_geral: 0, total_pago: 0, total_pendente: 0, total_atrasado: 0 });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ err: '', suc: '' });
  const [editId, setEditId] = useState(null);
  const [escopoEdicaoAberto, setEscopoEdicaoAberto] = useState(false);
  const [salvandoConta, setSalvandoConta] = useState(false);
  const salvandoContaRef = useRef(false);
  const [erroConta, setErroConta] = useState('');
  const [form, setForm] = useState(() => ({ descricao: '', categoria: '', valor: '', data_vencimento: hojeLocalISO(), fornecedor: '', recorrencia: 'unica', observacoes: '' }));
  const [pagarModal, setPagarModal] = useState(false);
  const [current, setCurrent] = useState(null);
  const [val, setVal] = useState('');
  const [dataPagamento, setDataPagamento] = useState(() => hojeLocalISO());
  const [historicoPagamentos, setHistoricoPagamentos] = useState([]);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [salvandoPagamento, setSalvandoPagamento] = useState(false);
  const [verContas, setVerContas] = useState(initialOpen);
  const [verResumo, setVerResumo] = useState(false);
  const [verPromissorias, setVerPromissorias] = useState(false);
  const [verVencidos, setVerVencidos] = useState(false);
  const [diasResumo, setDiasResumo] = useState(7);
  const [contasResumo, setContasResumo] = useState([]);
  const [resumoProximos, setResumoProximos] = useState({ total_geral: 0, total_pendente: 0, total_contas: 0 });
  const [contasVencidas, setContasVencidas] = useState([]);
  const [resumoVencidas, setResumoVencidas] = useState({ total_pendente: 0, total_contas: 0 });
  const [loadingResumo, setLoadingResumo] = useState(false);
  const [toggleLoadingId, setToggleLoadingId] = useState(null);
  const [modalContaAberto, setModalContaAberto] = useState(false);
  const [contaParaExcluir, setContaParaExcluir] = useState(null);
  const [excluindoConta, setExcluindoConta] = useState(false);
  const [contasSelecionadas, setContasSelecionadas] = useState([]);
  const [buscaContas, setBuscaContas] = useState('');
  const [buscaResumo, setBuscaResumo] = useState('');

  const fetchContas = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const lojaId = lojaContexto?._id || user?.loja?._id || user?.loja_id;
      const h = { 'x-auth-token': token };
      if (lojaId) h['x-loja-id'] = lojaId;
      const r = await axios.get(`${URL}/contas-pagar`, { params: { mes: m, ano: a, loja_id: lojaId }, headers: h });
      if (r.data.success) { setList(r.data.contas); setRes(r.data.resumo); }
    } catch (e) { setMsg(x => ({ ...x, err: 'Erro ao carregar' })); }
    finally { setLoading(false); }
  };

  const fetchResumo = async () => {
    setLoadingResumo(true);
    try {
      const token = localStorage.getItem('token');
      const lojaId = lojaContexto?._id || user?.loja?._id || user?.loja_id;
      const headers = { 'x-auth-token': token };
      if (lojaId) headers['x-loja-id'] = lojaId;
      const resposta = await axios.get(`${URL}/contas-pagar/resumo`, {
        params: { dias: diasResumo, loja_id: lojaId },
        headers
      });
      if (resposta.data.success) {
        setContasResumo(resposta.data.contas);
        setResumoProximos(resposta.data.resumo);
        setContasVencidas(resposta.data.contas_vencidas || []);
        setResumoVencidas(resposta.data.resumo_vencidas || { total_pendente: 0, total_contas: 0 });
      }
    } catch (_) {
      setMsg(x => ({ ...x, err: 'Erro ao carregar o resumo' }));
    } finally {
      setLoadingResumo(false);
    }
  };

  useEffect(() => {
    const lista = [];
    for (let i = -1; i < 12; i++) {
      const dt = new Date(); dt.setDate(1); dt.setMonth(dt.getMonth() - i);
      lista.push({ val: `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`, label: dt.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }) + (i === -1 ? ' (previsão)' : '') });
    }
    setOpcoesMes(lista);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => { fetchContas(); }, [mesAno, lojaContexto]);

  useEffect(() => {
    if (verResumo) fetchResumo();
  }, [verResumo, diasResumo, lojaContexto]);

  useEffect(() => {
    try {
      const catCache = JSON.parse(localStorage.getItem('contas_pagar_categorias') || '[]');
      const forCache = JSON.parse(localStorage.getItem('contas_pagar_fornecedores') || '[]');
      if (Array.isArray(catCache) && catCache.length) {
        setCategorias(prev => Array.from(new Set([...prev, ...catCache])));
      }
      if (Array.isArray(forCache) && forCache.length) {
        setFornecedoresRapidos(prev => Array.from(new Set([...prev, ...forCache])));
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    try {
      const catsDaLista = list.map(i => (i.categoria || '').trim()).filter(Boolean);
      const fornDaLista = list.map(i => (i.fornecedor || '').trim()).filter(Boolean);
      const catsUnicas = Array.from(new Set([...categorias, ...catsDaLista]));
      const fornUnicos = Array.from(new Set([...fornecedoresRapidos, ...fornDaLista]));
      setCategorias(catsUnicas);
      setFornecedoresRapidos(fornUnicos);
      localStorage.setItem('contas_pagar_categorias', JSON.stringify(catsUnicas));
      localStorage.setItem('contas_pagar_fornecedores', JSON.stringify(fornUnicos));
    } catch (_) {}
  }, [list]);

  useEffect(() => {
    setErroConta('');
    setEscopoEdicaoAberto(false);
  }, [modalContaAberto, editId]);

  const save = (e) => {
    e.preventDefault();
    if (salvandoContaRef.current) return;
    setErroConta('');
    const conta = list.find(item => item._id === editId);
    if (conta?.recorrencia && conta.recorrencia !== 'unica' && ['pendente', 'atrasado'].includes(conta.status) && !Number(conta.valor_pago) && !conta.data_pagamento) {
      setEscopoEdicaoAberto(true);
      return;
    }
    salvarConta('mes');
  };

  const salvarConta = async (escopo) => {
    if (salvandoContaRef.current) return;
    salvandoContaRef.current = true;
    setSalvandoConta(true);
    setErroConta('');
    try {
      const token = localStorage.getItem('token');
      const lojaId = lojaContexto?._id || user?.loja?._id || user?.loja_id;
      const h = { 'x-auth-token': token };
      if (lojaId) h['x-loja-id'] = lojaId;

      if (editId) {
        await axios.put(`${URL}/contas-pagar/${editId}`, { ...form, escopo }, { headers: h });
      } else {
        await axios.post(`${URL}/contas-pagar`, {
          ...form,
          valor: parseFloat(form.valor),
          loja_id: lojaId
        }, { headers: h });
      }

      const categoriaDigitada = (form.categoria || '').trim();
      const fornecedorDigitado = (form.fornecedor || '').trim();
      if (categoriaDigitada) {
        setCategorias(prev => {
          const next = Array.from(new Set([...prev, categoriaDigitada]));
          localStorage.setItem('contas_pagar_categorias', JSON.stringify(next));
          return next;
        });
      }
      if (fornecedorDigitado) {
        setFornecedoresRapidos(prev => {
          const next = Array.from(new Set([...prev, fornecedorDigitado]));
          localStorage.setItem('contas_pagar_fornecedores', JSON.stringify(next));
          return next;
        });
      }

      setModalContaAberto(false);
      setEscopoEdicaoAberto(false);
      setEditId(null);
      setForm(() => ({ descricao: '', categoria: '', valor: '', data_vencimento: hojeLocalISO(), fornecedor: '', recorrencia: 'unica', observacoes: '' }));
      fetchContas();
      setMsg({ err: '', suc: editId ? 'Conta atualizada!' : 'Conta salva com sucesso!' });
    } catch (e) {
      setErroConta(e.response?.data?.message || 'Erro ao salvar. Tente novamente.');
    } finally {
      salvandoContaRef.current = false;
      setSalvandoConta(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Excluir conta?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${URL}/contas-pagar/${id}`, { headers: { 'x-auth-token': token } });
      fetchContas(); setMsg({ err: '', suc: 'Excluído com sucesso!' });
    } catch (e) { setMsg(x => ({ ...x, err: 'Erro ao excluir' })); }
  };

  const abrirPagamento = async (conta) => {
    setCurrent(conta);
    const saldoDevedor = Number((conta.valor || 0) - (conta.valor_pago || 0));
    setVal(saldoDevedor > 0 ? saldoDevedor.toFixed(2) : Number(conta.valor || 0).toFixed(2));
    setDataPagamento(hojeLocalISO());
    setHistoricoPagamentos([]);
    setPagarModal(true);
    setLoadingHistorico(true);
    try {
      const token = localStorage.getItem('token');
      const resposta = await axios.get(`${URL}/contas-pagar/${conta._id}/historico`, {
        params: { limite: 6 },
        headers: { 'x-auth-token': token }
      });
      setHistoricoPagamentos(resposta.data.historico || []);
    } catch (_) {
      setMsg({ err: 'Não foi possível carregar o histórico de pagamentos', suc: '' });
    } finally {
      setLoadingHistorico(false);
    }
  };

  const pay = async (e) => {
    e.preventDefault();
    if (salvandoPagamento) return;
    setSalvandoPagamento(true);
    try {
      const token = localStorage.getItem('token');
      const resposta = await axios.post(`${URL}/contas-pagar/${current._id}/pagar`, {
        valor_pago: val,
        data_pagamento: dataPagamento
      }, { headers: { 'x-auth-token': token } });
      const contaAtualizada = resposta.data.conta;

      setList(anterior => {
        const proxima = anterior.map(item => item._id === contaAtualizada._id ? contaAtualizada : item);
        return proxima;
      });
      setContasResumo(anterior => {
        const proxima = anterior.filter(item => item._id !== contaAtualizada._id);
        setResumoProximos(calcularResumoLista(proxima));
        return proxima;
      });
      setContasVencidas(anterior => {
        const proxima = anterior.filter(item => item._id !== contaAtualizada._id);
        setResumoVencidas(calcularResumoLista(proxima));
        return proxima;
      });
      setPagarModal(false);
      setCurrent(null);
      setMsg({ err: '', suc: 'Pagamento registrado!' });
      await fetchContas();
    } catch (e) {
      setMsg(x => ({ ...x, err: e.response?.data?.message || 'Erro ao pagar' }));
    } finally {
      setSalvandoPagamento(false);
    }
  };

  const togglePago = async (conta) => {
    if (toggleLoadingId) return;
    setToggleLoadingId(conta._id);
    try {
      const token = localStorage.getItem('token');
      const resposta = await axios.post(`${URL}/contas-pagar/${conta._id}/toggle-status`, {}, { headers: { 'x-auth-token': token } });
      const contaAtualizada = resposta.data.conta;
      setList(anterior => {
        const proxima = anterior.map(item => item._id === contaAtualizada._id ? contaAtualizada : item);
        return proxima;
      });
      await fetchContas();
    } catch (e) {
      setMsg(x => ({ ...x, err: 'Erro ao alterar status' }));
    } finally {
      setToggleLoadingId(null);
    }
  };

  const filtrarConta = (c, termo) => {
    if (!termo) return true;
    const desc = normalizarTexto(c.descricao);
    const cat = normalizarTexto(c.categoria);
    const forn = normalizarTexto(c.fornecedor);
    const obs = normalizarTexto(c.observacoes);
    const valStr = normalizarTexto(String(c.valor || ''));
    const statusStr = normalizarTexto(c.status === 'pago' ? 'pago' : c.status === 'atrasado' ? 'atrasado' : 'pendente');
    const veiculo = normalizarTexto(c.quitacao?.veiculo);
    const placa = normalizarTexto(c.quitacao?.placa);
    const vendedor = normalizarTexto(c.quitacao?.vendedor);
    const inst = normalizarTexto(c.quitacao?.instituicao);

    return desc.includes(termo)
      || cat.includes(termo)
      || forn.includes(termo)
      || obs.includes(termo)
      || valStr.includes(termo)
      || statusStr.includes(termo)
      || veiculo.includes(termo)
      || placa.includes(termo)
      || vendedor.includes(termo)
      || inst.includes(termo);
  };

  const contasFiltradas = useMemo(() => {
    const termo = normalizarTexto(buscaContas.trim());
    const filtradas = termo ? list.filter(c => filtrarConta(c, termo)) : [...list];
    return filtradas.sort((a, b) =>
      Number(a.status === 'pago') - Number(b.status === 'pago') ||
      new Date(a.data_vencimento) - new Date(b.data_vencimento)
    );
  }, [list, buscaContas]);

  const contasVencidasFiltradas = useMemo(() => {
    const termo = normalizarTexto(buscaResumo.trim());
    if (!termo) return contasVencidas;
    return contasVencidas.filter(c => filtrarConta(c, termo));
  }, [contasVencidas, buscaResumo]);

  const contasResumoFiltradas = useMemo(() => {
    const termo = normalizarTexto(buscaResumo.trim());
    if (!termo) return contasResumo;
    return contasResumo.filter(c => filtrarConta(c, termo));
  }, [contasResumo, buscaResumo]);

  const solicitarExclusao = (id) => {
    const conta = list.find(item => item._id === id);
    if (conta) setContaParaExcluir(conta);
  };

  const deleteConta = async (escopo = 'mes') => {
    if (!contaParaExcluir || excluindoConta) return;
    setExcluindoConta(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${URL}/contas-pagar/${contaParaExcluir._id}`, {
        params: { escopo },
        headers: { 'x-auth-token': token }
      });
      setContaParaExcluir(null);
      setModalContaAberto(false);
      setEditId(null);
      fetchContas();
      setMsg({
        err: '',
        suc: escopo === 'serie' ? 'Todas as recorrências foram excluídas!' : 'Conta deste mês excluída!'
      });
    } catch (e) {
      setMsg(x => ({ ...x, err: 'Erro ao excluir' }));
    } finally {
      setExcluindoConta(false);
    }
  };

  const fmt = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

  const alternarSelecaoConta = (contaId) => {
    setContasSelecionadas(prev => prev.includes(contaId)
      ? prev.filter(id => id !== contaId)
      : [...prev, contaId]
    );
  };

  const limparSelecaoContas = () => setContasSelecionadas([]);

  const copiarContasSelecionadas = async () => {
    const selecionadas = list.filter(c => contasSelecionadas.includes(c._id));
    if (!selecionadas.length) return;

    const porVencimento = selecionadas.reduce((acc, conta) => {
      const venc = formatarDataLocal(conta.data_vencimento);
      if (!acc[venc]) acc[venc] = [];
      acc[venc].push(conta);
      return acc;
    }, {});

    const blocos = Object.entries(porVencimento).map(([vencimento, contas]) => {
      const itens = contas.map(c => {
        const partes = [
          `Descrição: ${c.descricao || '-'}`,
          `Fornecedor: ${c.fornecedor || '-'}`,
          `Valor: ${fmt(c.valor)}`
        ];
        if (c.observacoes?.trim()) partes.push(`Observações: ${c.observacoes.trim()}`);
        return partes.join('\n');
      }).join('\n\n');

      return `Contas vencendo em ${vencimento}\n\n${itens}`;
    });

    const texto = blocos.join('\n\n--------------------\n\n');

    try {
      await navigator.clipboard.writeText(texto);
      setMsg({ err: '', suc: `${selecionadas.length} conta(s) copiadas!` });
    } catch (_) {
      setMsg({ err: 'Não foi possível copiar as contas selecionadas', suc: '' });
    }
  };

  return (
    <div className="content-section analise-page" style={{ background: 'linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)' }}>
      <div className="page-header-mobile">
        <button className="mobile-menu-toggle" onClick={onToggleSidebar}>☰</button>
        <h1>Financeiro</h1>
      </div>

      {!verContas ? (
        <div className="analise-tool-groups">
          <section className="analise-tool-group" aria-labelledby="grupo-contas">
            <div className="analise-tool-group-header">
              <h2 id="grupo-contas">Contas</h2>
            </div>
            <div className="analise-tools-grid">
              <ToolCard label="Contas a pagar" icon={<IconeContasPagar />} onClick={() => setVerContas(true)} />
              <ToolCard label="Resumo" icon={<IconeResumo />} onClick={() => setVerResumo(true)} />
              <ToolCard label="Promissórias" icon={<IconePromissorias />} onClick={() => setVerPromissorias(true)} />
              <ToolCard label="Vencidos" icon={<IconeVencidos />} onClick={() => setVerVencidos(true)} />
            </div>
          </section>
        </div>
      ) : (
        <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)', display: 'flex', flexDirection: 'column' }}>
          {/* Cabeçalho */}
          <div style={{ display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between', padding: isMobile ? '14px 16px' : '18px 24px', borderBottom: '1px solid #e2e8f0', flexShrink: 0, flexDirection: isMobile ? 'column' : 'row', gap: '12px', background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(8px)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', justifyContent: isMobile ? 'space-between' : 'flex-start' }}>
              <span style={{ color: '#475569', display: 'flex' }}><IconeContasPagar /></span>
              <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '700', color: '#1e293b' }}>Contas a Pagar</h2>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', justifyContent: isMobile ? 'stretch' : 'flex-end', width: isMobile ? '100%' : 'auto' }}>
              <div style={{ position: 'relative', flex: isMobile ? '1 1 100%' : '0 1 280px', minWidth: isMobile ? '100%' : '240px' }}>
                <input
                  type="text"
                  value={buscaContas}
                  onChange={e => setBuscaContas(e.target.value)}
                  placeholder="Buscar descrição, categoria, fornecedor..."
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '10px 34px 10px 36px',
                    borderRadius: '10px',
                    border: '1px solid #cbd5e1',
                    background: '#fff',
                    fontSize: '0.85rem',
                    color: '#1e293b',
                    outline: 'none'
                  }}
                />
                <span style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', display: 'flex', pointerEvents: 'none' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                </span>
                {buscaContas && (
                  <button
                    type="button"
                    onClick={() => setBuscaContas('')}
                    title="Limpar busca"
                    style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', lineHeight: 1, padding: '2px 6px' }}
                  >
                    ×
                  </button>
                )}
              </div>
              <select value={mesAno} onChange={e => setMesAno(e.target.value)}
                style={{ padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '0.85rem', color: '#475569', background: '#f8fafc', cursor: 'pointer', textTransform: 'capitalize', minWidth: isMobile ? '100%' : '170px' }}>
                {opcoesMes.map(o => <option key={o.val} value={o.val} style={{ textTransform: 'capitalize' }}>{o.label}</option>)}
              </select>
              <button onClick={() => { setEditId(null); setForm({ descricao: '', categoria: '', valor: '', data_vencimento: hojeLocalISO(), fornecedor: '', recorrencia: 'unica', observacoes: '' }); setModalContaAberto(true); }}
                className="btn-primary" style={{ padding: '10px 18px', fontSize: '0.88rem', borderRadius: '10px', flex: isMobile ? '1 1 auto' : '0 0 auto' }}>
                + Criar pagamento
              </button>
              <button onClick={() => setVerContas(false)} title="Voltar ao menu Financeiro" style={{ background: '#eef2ff', border: '1px solid #c7d2fe', fontSize: '1.1rem', cursor: 'pointer', color: '#4338ca', lineHeight: 1, width: '42px', height: '42px', borderRadius: '12px', fontWeight: 700, flexShrink: 0 }}>×</button>
            </div>
          </div>

          {/* Fora da área rolável: ações sempre visíveis enquanto houver seleção. */}
        {contasSelecionadas.length > 0 && (
          <div
            className="contas-selection-bar"
            role="group"
            aria-label="Ações das contas selecionadas"
            style={{
              display: 'flex',
              flexShrink: 0,
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              padding: isMobile ? '10px 16px' : '10px 24px',
              background: '#f8fafc',
              borderBottom: '1px solid #e5e7eb'
            }}
          >
            <span style={{ color: '#374151', fontWeight: 600 }}>
              Selecionadas: <strong>{contasSelecionadas.length}</strong>
            </span>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={copiarContasSelecionadas}
                style={{
                  padding: '10px 14px',
                  background: '#2563eb',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                Copiar seleção
              </button>
              <button
                type="button"
                onClick={limparSelecaoContas}
                style={{
                  padding: '10px 14px',
                  background: '#e2e8f0',
                  color: '#334155',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                Limpar
              </button>
            </div>
          </div>
        )}

          {/* Corpo rolável */}
          <div className="contas-lista-rolavel" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: isMobile ? '12px' : '0 24px 24px' }}>
            {msg.err && <div style={{ padding: '12px 14px', background: '#fee2e2', color: '#991b1b', borderRadius: '12px', marginTop: '16px', marginBottom: '12px', border: '1px solid #fecaca' }}>{msg.err}</div>}
            {msg.suc && <div style={{ padding: '12px 14px', background: '#dcfce7', color: '#166534', borderRadius: '12px', marginTop: '16px', marginBottom: '12px', border: '1px solid #bbf7d0' }}>{msg.suc}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginTop: '1rem', marginBottom: '20px' }}>
        <div style={{ background: 'white', padding: '16px', borderRadius: '16px', border: '1px solid #dbeafe', boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b' }}>TOTAL</div>
          <div style={{ fontSize: '1.3rem', fontWeight: '700', color: '#1e293b' }}>{fmt(res.total_geral)}</div>
        </div>
        <div title="Valores efetivamente pagos no mês selecionado, independentemente do vencimento" style={{ background: 'white', padding: '16px', borderRadius: '16px', border: '1px solid #bbf7d0', boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b' }}>PAGO</div>
          <div style={{ fontSize: '1.3rem', fontWeight: '700', color: '#10b981' }}>{fmt(res.total_pago)}</div>
          <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '4px' }}>Pela data do pagamento</div>
        </div>
        <div style={{ background: 'white', padding: '16px', borderRadius: '16px', border: '1px solid #fde68a', boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b' }}>PENDENTE</div>
          <div style={{ fontSize: '1.3rem', fontWeight: '700', color: '#d97706' }}>{fmt(res.total_pendente)}</div>
        </div>
        <div style={{ background: 'white', padding: '16px', borderRadius: '16px', border: '1px solid #fecaca', boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b' }}>ATRASADO</div>
          <div style={{ fontSize: '1.3rem', fontWeight: '700', color: '#ef4444' }}>{fmt(res.total_atrasado)}</div>
        </div>
      </div>

      <div style={{ background: 'white', borderRadius: '18px', boxShadow: '0 10px 30px rgba(15,23,42,0.05)', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
        {loading ? <div style={{ padding: '30px', textAlign: 'center' }}>Carregando...</div> :
        list.length === 0 ? <div style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>Nenhuma conta a pagar neste mês.</div> :
        contasFiltradas.length === 0 ? <div style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>Nenhuma conta encontrada com o termo "{buscaContas}".</div> :
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem', textAlign: 'left' }}>
            <thead>
              <tr style={{ background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
                <th style={{ padding: '12px', width: '44px' }}></th>
                <th style={{ padding: '12px' }}>Status</th>
                <th style={{ padding: '12px' }}>Vencimento</th>
                <th style={{ padding: '12px' }}>Descrição</th>
                <th style={{ padding: '12px' }}>Categoria</th>
                <th style={{ padding: '12px' }}>Fornecedor</th>
                <th style={{ padding: '12px', textAlign: 'right' }}>Valor</th>
                <th style={{ padding: '12px', textAlign: 'center' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {contasFiltradas.map((c, indice) => {
                const st = c.status === 'pago' ? { bg: '#dcfce7', c: '#166534', l: 'Pago' } :
                           c.status === 'atrasado' ? { bg: '#fee2e2', c: '#991b1b', l: 'Atrasado' } :
                           { bg: '#fef9c3', c: '#854d0e', l: 'Pendente' };
                const importadaPlanilha = (c.origem_importacao || '').toUpperCase().includes('PLANILHA');
                const tooltipDescricao = [c.descricao, c.observacoes?.trim() ? `Observações: ${c.observacoes.trim()}` : null].filter(Boolean).join('\n\n');
                return (
                  <React.Fragment key={c._id}>
                  {c.status === 'pago' && (indice === 0 || contasFiltradas[indice - 1].status !== 'pago') && (
                    <tr className="contas-divisor-pagas">
                      <th colSpan={8} style={{ padding: '14px 12px', background: '#f0fdf4', color: '#166534', borderTop: '2px solid #86efac', borderBottom: '1px solid #bbf7d0', textAlign: 'left', fontWeight: 700 }}>
                        Contas pagas
                      </th>
                    </tr>
                  )}
                  <tr style={{ borderBottom: '1px solid #f1f5f9', background: importadaPlanilha ? '#eff6ff' : 'transparent', boxShadow: importadaPlanilha ? 'inset 4px 0 0 #3b82f6' : 'none' }}>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={contasSelecionadas.includes(c._id)}
                        onChange={() => alternarSelecaoConta(c._id)}
                        aria-label={`Selecionar conta ${c.descricao}`}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                    </td>
                    <td style={{ padding: '12px' }}><span style={{ padding: '3px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600', background: st.bg, color: st.c }}>{st.l}</span></td>
                    <td style={{ padding: '12px' }}>{formatarDataLocal(c.data_vencimento)}</td>
                    <td title={tooltipDescricao} style={{ padding: '12px', fontWeight: '600', color: importadaPlanilha ? '#1d4ed8' : '#1e293b' }}>
                      {c.descricao}
                      <DetalhesQuitacao conta={c} />
                      {importadaPlanilha && (
                        <span style={{ marginLeft: '8px', fontSize: '0.72rem', fontWeight: 700, color: '#2563eb', background: '#dbeafe', padding: '3px 8px', borderRadius: '999px' }}>
                          WEB
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '12px' }}>{c.categoria}</td>
                    <td style={{ padding: '12px' }}>{c.fornecedor || '-'}</td>
                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: '700' }}>{fmt(c.valor)}</td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '6px' }}>
                        <button disabled={toggleLoadingId === c._id} onClick={() => c.status === 'pago' ? togglePago(c) : abrirPagamento(c)} style={{ background: c.status === 'pago' ? '#dcfce7' : '#fee2e2', color: c.status === 'pago' ? '#166534' : '#991b1b', border: 'none', padding: '5px 10px', borderRadius: '999px', cursor: toggleLoadingId === c._id ? 'wait' : 'pointer', opacity: toggleLoadingId === c._id ? 0.65 : 1, fontSize: '0.8rem', fontWeight: '700', minWidth: '88px' }}>
                          {toggleLoadingId === c._id ? 'Salvando...' : (c.status === 'pago' ? 'Pago' : 'Não pago')}
                        </button>
                        <button onClick={() => { setEditId(c._id); setForm({ descricao: c.descricao, categoria: c.categoria, valor: c.valor, data_vencimento: dataParaInputLocal(c.data_vencimento), fornecedor: c.fornecedor || '', recorrencia: c.recorrencia || 'unica', observacoes: c.observacoes || '' }); setModalContaAberto(true); }} style={{ background: '#e2e8f0', border: 'none', padding: '5px 8px', borderRadius: '4px', cursor: 'pointer' }}>✏️</button>
                      </div>
                    </td>
                  </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>}
      </div>
      </div>
      </div>
      )}

      {verResumo && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between', padding: isMobile ? '14px 16px' : '18px 24px', borderBottom: '1px solid #e2e8f0', flexDirection: isMobile ? 'column' : 'row', gap: '12px', background: 'rgba(255,255,255,0.9)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ color: '#475569', display: 'flex' }}><IconeResumo /></span>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.15rem', color: '#1e293b' }}>Resumo</h2>
                <div style={{ color: '#64748b', fontSize: '0.82rem', marginTop: '3px' }}>Contas com vencimento próximo</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'stretch' : 'flex-end' }}>
              <div style={{ position: 'relative', flex: isMobile ? '1 1 100%' : '0 1 260px', minWidth: isMobile ? '100%' : '220px' }}>
                <input
                  type="text"
                  value={buscaResumo}
                  onChange={e => setBuscaResumo(e.target.value)}
                  placeholder="Buscar contas no resumo..."
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '9px 34px 9px 36px',
                    borderRadius: '10px',
                    border: '1px solid #cbd5e1',
                    background: '#fff',
                    fontSize: '0.85rem',
                    color: '#1e293b',
                    outline: 'none'
                  }}
                />
                <span style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', display: 'flex', pointerEvents: 'none' }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                </span>
                {buscaResumo && (
                  <button
                    type="button"
                    onClick={() => setBuscaResumo('')}
                    title="Limpar busca"
                    style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', lineHeight: 1, padding: '2px 6px' }}
                  >
                    ×
                  </button>
                )}
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#475569', fontSize: '0.85rem', fontWeight: 600 }}>
                Próximos
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={diasResumo}
                  onChange={e => setDiasResumo(Math.min(365, Math.max(1, Number(e.target.value) || 1)))}
                  style={{ width: '72px', padding: '9px 10px', border: '1px solid #cbd5e1', borderRadius: '10px' }}
                />
                dias
              </label>
              <button onClick={() => setVerResumo(false)} title="Voltar ao menu Financeiro" style={{ background: '#eef2ff', border: '1px solid #c7d2fe', fontSize: '1.1rem', cursor: 'pointer', color: '#4338ca', width: '42px', height: '42px', borderRadius: '12px', fontWeight: 700, flexShrink: 0 }}>×</button>
            </div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '14px 12px 24px' : '22px 24px' }}>
            {msg.err && <div style={{ padding: '12px 14px', background: '#fee2e2', color: '#991b1b', borderRadius: '12px', marginBottom: '14px', border: '1px solid #fecaca' }}>{msg.err}</div>}
            {msg.suc && <div style={{ padding: '12px 14px', background: '#dcfce7', color: '#166534', borderRadius: '12px', marginBottom: '14px', border: '1px solid #bbf7d0' }}>{msg.suc}</div>}

            <section style={{ background: '#fff7f7', border: '2px solid #fca5a5', borderRadius: '18px', overflow: 'hidden', marginBottom: '24px', boxShadow: '0 10px 28px rgba(185,28,28,0.09)' }} aria-labelledby="titulo-contas-vencidas">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '14px', flexWrap: 'wrap', padding: '16px 18px', background: '#fee2e2', borderBottom: '1px solid #fca5a5' }}>
                <div>
                  <h3 id="titulo-contas-vencidas" style={{ margin: 0, color: '#991b1b', fontSize: '1.05rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Vencidas</h3>
                  <div style={{ color: '#b91c1c', fontSize: '0.8rem', marginTop: '3px' }}>Contas anteriores que ainda não foram pagas</div>
                </div>
                <div style={{ display: 'flex', gap: '9px', flexWrap: 'wrap' }}>
                  <span style={{ padding: '7px 10px', borderRadius: '999px', background: '#fff', color: '#991b1b', fontSize: '0.8rem', fontWeight: 750 }}>{resumoVencidas.total_contas || 0} conta(s)</span>
                  <span style={{ padding: '7px 10px', borderRadius: '999px', background: '#991b1b', color: '#fff', fontSize: '0.8rem', fontWeight: 800 }}>{fmt(resumoVencidas.total_pendente)}</span>
                </div>
              </div>

              {loadingResumo ? (
                <div style={{ padding: '26px', textAlign: 'center', color: '#991b1b' }}>Carregando contas vencidas...</div>
              ) : contasVencidas.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: '#64748b', background: '#fff' }}>Nenhuma conta vencida em aberto.</div>
              ) : contasVencidasFiltradas.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: '#64748b', background: '#fff' }}>Nenhuma conta vencida encontrada com o termo "{buscaResumo}".</div>
              ) : (
                <div style={{ overflowX: 'auto', background: '#fff' }}>
                  <table style={{ width: '100%', minWidth: '760px', borderCollapse: 'collapse', fontSize: '0.9rem', textAlign: 'left' }}>
                    <thead>
                      <tr style={{ background: '#fff1f2', color: '#9f1239' }}>
                        <th style={{ padding: '13px' }}>Vencimento</th>
                        <th style={{ padding: '13px' }}>Atraso</th>
                        <th style={{ padding: '13px' }}>Descrição</th>
                        <th style={{ padding: '13px' }}>Fornecedor</th>
                        <th style={{ padding: '13px', textAlign: 'right' }}>Em aberto</th>
                        <th style={{ padding: '13px', textAlign: 'center' }}>Ação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contasVencidasFiltradas.map(conta => {
                        const diasAtraso = calcularDiasEmAtraso(conta.data_vencimento);
                        return (
                          <tr key={conta._id} style={{ borderTop: '1px solid #fee2e2' }}>
                            <td style={{ padding: '13px', whiteSpace: 'nowrap', color: '#991b1b', fontWeight: 750 }}>{formatarDataLocal(conta.data_vencimento)}</td>
                            <td style={{ padding: '13px', color: '#b91c1c', fontWeight: 700 }}>{diasAtraso} {diasAtraso === 1 ? 'dia' : 'dias'}</td>
                            <td style={{ padding: '13px', fontWeight: 700, color: '#1e293b' }}>{conta.descricao}<DetalhesQuitacao conta={conta} /></td>
                            <td style={{ padding: '13px' }}>{conta.fornecedor || '-'}</td>
                            <td style={{ padding: '13px', textAlign: 'right', color: '#991b1b', fontWeight: 800 }}>{fmt((conta.valor || 0) - (conta.valor_pago || 0))}</td>
                            <td style={{ padding: '13px', textAlign: 'center' }}>
                              <button type="button" onClick={() => abrirPagamento(conta)} style={{ padding: '7px 12px', border: 0, borderRadius: '999px', background: '#dc2626', color: '#fff', fontWeight: 750, cursor: 'pointer', whiteSpace: 'nowrap' }}>Registrar pagamento</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <h3 style={{ margin: '0 0 12px', color: '#334155', fontSize: '1.05rem' }}>Próximos vencimentos</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '16px', marginBottom: '20px' }}>
              <div style={{ background: '#fff', padding: '18px', borderRadius: '16px', border: '1px solid #dbeafe' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b' }}>CONTAS A VENCER</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#2563eb', marginTop: '4px' }}>{resumoProximos.total_contas || 0}</div>
              </div>
              <div style={{ background: '#fff', padding: '18px', borderRadius: '16px', border: '1px solid #fde68a' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b' }}>TOTAL PREVISTO</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#d97706', marginTop: '4px' }}>{fmt(resumoProximos.total_pendente)}</div>
              </div>
            </div>

            <div style={{ background: '#fff', borderRadius: '18px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 10px 30px rgba(15,23,42,0.05)' }}>
              {loadingResumo ? (
                <div style={{ padding: '34px', textAlign: 'center', color: '#64748b' }}>Carregando resumo...</div>
              ) : contasResumo.length === 0 ? (
                <div style={{ padding: '34px', textAlign: 'center', color: '#64748b' }}>Nenhuma conta vencendo nos próximos {diasResumo} dias.</div>
              ) : contasResumoFiltradas.length === 0 ? (
                <div style={{ padding: '34px', textAlign: 'center', color: '#64748b' }}>Nenhuma conta a vencer encontrada com o termo "{buscaResumo}".</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', minWidth: '680px', borderCollapse: 'collapse', fontSize: '0.9rem', textAlign: 'left' }}>
                    <thead>
                      <tr style={{ background: '#f1f5f9', color: '#475569' }}>
                        <th style={{ padding: '13px' }}>Vencimento</th>
                        <th style={{ padding: '13px' }}>Descrição</th>
                        <th style={{ padding: '13px' }}>Categoria</th>
                        <th style={{ padding: '13px' }}>Fornecedor</th>
                        <th style={{ padding: '13px', textAlign: 'right' }}>Valor</th>
                        <th style={{ padding: '13px', textAlign: 'center' }}>Ação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contasResumoFiltradas.map(conta => (
                        <tr key={conta._id} style={{ borderTop: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '13px', whiteSpace: 'nowrap' }}>{formatarDataLocal(conta.data_vencimento)}</td>
                          <td style={{ padding: '13px', fontWeight: 700, color: '#1e293b' }}>{conta.descricao}<DetalhesQuitacao conta={conta} /></td>
                          <td style={{ padding: '13px' }}>{conta.categoria || '-'}</td>
                          <td style={{ padding: '13px' }}>{conta.fornecedor || '-'}</td>
                          <td style={{ padding: '13px', textAlign: 'right', fontWeight: 800 }}>{fmt((conta.valor || 0) - (conta.valor_pago || 0))}</td>
                          <td style={{ padding: '13px', textAlign: 'center' }}>
                            <button type="button" onClick={() => abrirPagamento(conta)} style={{ padding: '7px 12px', border: '1px solid #86efac', borderRadius: '999px', background: '#dcfce7', color: '#166534', fontWeight: 750, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              Marcar como pago
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {verPromissorias && (
        <Promissorias
          user={user}
          lojaContexto={lojaContexto}
          onClose={() => setVerPromissorias(false)}
          onToggleSidebar={onToggleSidebar}
          onAbrirNegociacao={onAbrirNegociacao}
        />
      )}
      {verVencidos && <Vencidos user={user} lojaContexto={lojaContexto} onClose={() => setVerVencidos(false)} />}

      {modalContaAberto && (
        <div style={{ position: 'fixed', inset: 0, background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)', zIndex: 3100, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: isMobile ? '14px 16px' : '18px 24px', borderBottom: '1px solid #e2e8f0', background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(8px)', flexShrink: 0 }}>
            <div>
              <h2 style={{ fontSize: '1.2rem', fontWeight: '700', margin: 0, color: '#0f172a' }}>{editId ? 'Editar pagamento' : 'Criar pagamento'}</h2>
              <div style={{ fontSize: '0.82rem', color: '#64748b', marginTop: '4px' }}>Cadastre, edite e organize tudo nesta mesma tela.</div>
            </div>
            <button type="button" disabled={salvandoConta} onClick={() => setModalContaAberto(false)} style={{ background: '#eef2ff', border: '1px solid #c7d2fe', width: '40px', height: '40px', borderRadius: '12px', cursor: 'pointer', color: '#4338ca', fontWeight: 700 }}>×</button>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '14px 16px 24px' : '24px' }}>
            <form onSubmit={save} style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '1100px', margin: '0 auto' }}>
              {erroConta && <div role="alert" style={{ color: '#b91c1c', background: '#fee2e2', padding: '12px', borderRadius: '10px' }}>{erroConta}</div>}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#334155' }}>Descrição *</label>
                  <input type="text" required value={form.descricao} onChange={e => setForm({...form, descricao: e.target.value})} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #cbd5e1', marginTop: '6px' }} />
                </div>
                <div>
                  <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#334155' }}>Valor (R$) *</label>
                  <input type="number" step="0.01" required value={form.valor} onChange={e => setForm({...form, valor: e.target.value})} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #cbd5e1', marginTop: '6px' }} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#334155' }}>Categoria</label>
                  <input list="lista-categorias" value={form.categoria} placeholder="Selecione ou digite uma categoria" onChange={e => setForm({...form, categoria: e.target.value})} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #cbd5e1', marginTop: '6px' }} />
                  <datalist id="lista-categorias">
                    {categorias.map(c => <option key={c} value={c} />)}
                  </datalist>
                </div>
                <div>
                  <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#334155' }}>Fornecedor</label>
                  <input list="lista-fornecedores" value={form.fornecedor} onChange={e => setForm({...form, fornecedor: e.target.value})} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #cbd5e1', marginTop: '6px' }} />
                  <datalist id="lista-fornecedores">
                    {fornecedoresRapidos.map(f => <option key={f} value={f} />)}
                  </datalist>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#334155' }}>Vencimento *</label>
                  <input type="date" required value={form.data_vencimento} onChange={e => setForm({...form, data_vencimento: e.target.value})} style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #cbd5e1', marginTop: '6px' }} />
                </div>
                <div>
                  <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#334155' }}>Recorrência</label>
                  <select disabled={Boolean(editId)} value={form.recorrencia} onChange={e => setForm({...form, recorrencia: e.target.value})} style={{ width: '100%', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '12px', fontSize: '0.85rem', color: '#475569', background: editId ? '#e2e8f0' : '#f8fafc', cursor: editId ? 'not-allowed' : 'pointer', marginTop: '6px' }}>
                    <option value="unica">Única</option>
                    <option value="mensal">Mensal</option>
                    <option value="bimestral">Bimestral</option>
                    <option value="trimestral">Trimestral</option>
                    <option value="semestral">Semestral</option>
                    <option value="anual">Anual</option>
                  </select>
                  {editId && <div style={{ marginTop: '5px', fontSize: '0.75rem', color: '#64748b' }}>A recorrência é definida na criação da conta.</div>}
                </div>
              </div>

              <div>
                <label style={{ fontSize: '0.85rem', fontWeight: '600', color: '#334155' }}>Observações</label>
                <textarea value={form.observacoes} onChange={e => setForm({...form, observacoes: e.target.value})} rows="4" style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #cbd5e1', marginTop: '6px', resize: 'vertical' }} />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
                {editId && (
                  <button type="button" disabled={salvandoConta} onClick={() => solicitarExclusao(editId)} style={{ background: '#fee2e2', color: '#991b1b', border: 'none', padding: '12px 16px', borderRadius: '12px', cursor: 'pointer', fontWeight: '700' }}>Excluir</button>
                )}
                <button type="button" disabled={salvandoConta} onClick={() => setModalContaAberto(false)} style={{ background: '#e2e8f0', border: 'none', padding: '12px 16px', borderRadius: '12px', cursor: 'pointer' }}>Cancelar</button>
                <button type="submit" disabled={salvandoConta} style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '12px 18px', borderRadius: '12px', cursor: 'pointer', fontWeight: '600' }}>{salvandoConta ? 'Salvando...' : editId ? 'Salvar alterações' : 'Criar pagamento'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {escopoEdicaoAberto && modalContaAberto && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 3200, background: 'rgba(15,23,42,0.55)', display: 'grid', placeItems: 'center', padding: '18px' }}>
          <div role="dialog" aria-modal="true" aria-labelledby="titulo-escopo-edicao" style={{ width: 'min(490px, 100%)', background: '#fff', borderRadius: '18px', padding: '24px' }}>
            <h2 id="titulo-escopo-edicao" style={{ margin: '0 0 12px', color: '#0f172a', fontSize: '1.2rem' }}>Editar conta recorrente</h2>
            <p style={{ color: '#64748b', lineHeight: 1.5 }}>Deseja aplicar as alterações somente à ocorrência selecionada ou também às próximas?</p>
            <p style={{ color: '#64748b', lineHeight: 1.5 }}>As próximas ocorrências já pagas, com pagamento parcial ou canceladas serão preservadas.</p>
            {erroConta && <p role="alert" style={{ color: '#b91c1c' }}>{erroConta}</p>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button type="button" disabled={salvandoConta} onClick={() => salvarConta('mes')} style={{ padding: '13px', border: '1px solid #cbd5e1', borderRadius: '11px', background: '#fff', color: '#334155', cursor: 'pointer' }}>Somente este mês</button>
              <button type="button" disabled={salvandoConta} onClick={() => salvarConta('proximas')} style={{ padding: '13px', border: 0, borderRadius: '11px', background: '#2563eb', color: '#fff', cursor: 'pointer' }}>Esta e as próximas não pagas</button>
              <button type="button" disabled={salvandoConta} onClick={() => setEscopoEdicaoAberto(false)} style={{ padding: '11px', border: 0, borderRadius: '10px', background: '#e2e8f0', color: '#334155', cursor: 'pointer' }}>Voltar à edição</button>
              {salvandoConta && <span role="status">Salvando alterações...</span>}
            </div>
          </div>
        </div>
      )}

      {contaParaExcluir && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 3200, background: 'rgba(15,23,42,0.55)', display: 'grid', placeItems: 'center', padding: '18px', backdropFilter: 'blur(3px)' }}>
          <div role="dialog" aria-modal="true" aria-labelledby="titulo-excluir-conta" style={{ width: 'min(470px, 100%)', background: '#fff', borderRadius: '18px', padding: '24px', boxShadow: '0 24px 60px rgba(15,23,42,0.25)' }}>
            <h2 id="titulo-excluir-conta" style={{ margin: '0 0 8px', color: '#0f172a', fontSize: '1.2rem' }}>Excluir conta</h2>
            <p style={{ margin: '0 0 20px', color: '#64748b', lineHeight: 1.5 }}>
              <strong style={{ color: '#334155' }}>{contaParaExcluir.descricao}</strong>
              <br />Vencimento: {formatarDataLocal(contaParaExcluir.data_vencimento)}
            </p>

            {contaParaExcluir.recorrencia && contaParaExcluir.recorrencia !== 'unica' && contaParaExcluir.serie_id ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button type="button" disabled={excluindoConta} onClick={() => deleteConta('mes')} style={{ padding: '13px 16px', border: '1px solid #fca5a5', borderRadius: '11px', background: '#fff', color: '#b91c1c', fontWeight: 750, cursor: excluindoConta ? 'wait' : 'pointer', textAlign: 'left' }}>
                  Excluir apenas deste mês
                  <span style={{ display: 'block', color: '#64748b', fontSize: '0.78rem', fontWeight: 500, marginTop: '3px' }}>Os próximos lançamentos continuarão normalmente.</span>
                </button>
                <button type="button" disabled={excluindoConta} onClick={() => deleteConta('serie')} style={{ padding: '13px 16px', border: 0, borderRadius: '11px', background: '#dc2626', color: '#fff', fontWeight: 750, cursor: excluindoConta ? 'wait' : 'pointer', textAlign: 'left' }}>
                  Excluir todas as recorrências
                  <span style={{ display: 'block', color: '#fee2e2', fontSize: '0.78rem', fontWeight: 500, marginTop: '3px' }}>Remove este mês e todos os demais lançamentos da série.</span>
                </button>
              </div>
            ) : (
              <button type="button" disabled={excluindoConta} onClick={() => deleteConta('mes')} style={{ width: '100%', padding: '13px 16px', border: 0, borderRadius: '11px', background: '#dc2626', color: '#fff', fontWeight: 750, cursor: excluindoConta ? 'wait' : 'pointer' }}>
                {excluindoConta ? 'Excluindo...' : 'Confirmar exclusão'}
              </button>
            )}

            <button type="button" disabled={excluindoConta} onClick={() => setContaParaExcluir(null)} style={{ width: '100%', marginTop: '10px', padding: '11px', border: 0, borderRadius: '10px', background: '#e2e8f0', color: '#334155', fontWeight: 650, cursor: excluindoConta ? 'not-allowed' : 'pointer' }}>Cancelar</button>
          </div>
        </div>
      )}

      {pagarModal && current && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.58)', display: 'grid', placeItems: 'center', zIndex: 3300, padding: '16px', backdropFilter: 'blur(3px)' }}>
          <div role="dialog" aria-modal="true" aria-labelledby="titulo-pagar-conta" style={{ background: '#fff', width: 'min(620px, 100%)', maxHeight: '92vh', overflowY: 'auto', borderRadius: '18px', padding: isMobile ? '20px 16px' : '24px', boxShadow: '0 24px 60px rgba(15,23,42,0.28)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '14px', marginBottom: '18px' }}>
              <div>
                <h2 id="titulo-pagar-conta" style={{ fontSize: '1.2rem', fontWeight: 750, margin: 0, color: '#0f172a' }}>Registrar pagamento</h2>
                <div style={{ color: '#64748b', fontSize: '0.86rem', marginTop: '5px' }}>{current.descricao}</div>
                <DetalhesQuitacao conta={current} />
              </div>
              <button type="button" disabled={salvandoPagamento} onClick={() => setPagarModal(false)} style={{ flex: '0 0 auto', width: '38px', height: '38px', border: 0, borderRadius: '10px', background: '#eef2ff', color: '#4338ca', fontSize: '1.15rem', cursor: 'pointer' }}>×</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px', marginBottom: '18px' }}>
              <div style={{ padding: '12px', borderRadius: '12px', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <div style={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 700 }}>VALOR PREVISTO</div>
                <div style={{ color: '#1e293b', fontWeight: 800, marginTop: '4px' }}>{fmt(current.valor)}</div>
              </div>
              <div style={{ padding: '12px', borderRadius: '12px', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <div style={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 700 }}>VENCIMENTO</div>
                <div style={{ color: '#1e293b', fontWeight: 800, marginTop: '4px' }}>{formatarDataLocal(current.data_vencimento)}</div>
              </div>
            </div>

            <form onSubmit={pay}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#334155' }}>
                    Valor efetivamente pago (R$) *
                  </label>
                  <input
                    autoFocus
                    type="number"
                    min="0.01"
                    step="0.01"
                    required
                    value={val}
                    onChange={e => setVal(e.target.value)}
                    style={{ width: '100%', boxSizing: 'border-box', marginTop: '7px', padding: '12px', borderRadius: '11px', border: '1px solid #94a3b8', fontSize: '1rem', fontWeight: 700 }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#334155' }}>
                    Data do pagamento *
                  </label>
                  <input
                    type="date"
                    required
                    value={dataPagamento}
                    onChange={e => setDataPagamento(e.target.value)}
                    style={{ width: '100%', boxSizing: 'border-box', marginTop: '7px', padding: '12px', borderRadius: '11px', border: '1px solid #94a3b8', fontSize: '0.95rem', fontWeight: 600, color: '#1e293b' }}
                  />
                </div>
              </div>
              <div style={{ color: '#64748b', fontSize: '0.78rem', marginTop: '8px' }}>
                Informe o valor real e a data em que o pagamento foi efetuado.
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '18px' }}>
                <button type="button" disabled={salvandoPagamento} onClick={() => setPagarModal(false)} style={{ background: '#e2e8f0', color: '#334155', border: 0, padding: '11px 15px', borderRadius: '10px', cursor: salvandoPagamento ? 'not-allowed' : 'pointer', fontWeight: 650 }}>Cancelar</button>
                <button type="submit" disabled={salvandoPagamento} style={{ background: '#16a34a', color: '#fff', border: 0, padding: '11px 17px', borderRadius: '10px', cursor: salvandoPagamento ? 'wait' : 'pointer', fontWeight: 750, opacity: salvandoPagamento ? 0.7 : 1 }}>{salvandoPagamento ? 'Registrando...' : 'Confirmar pagamento'}</button>
              </div>
            </form>

            <div style={{ marginTop: '24px', paddingTop: '18px', borderTop: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: '0 0 12px', color: '#334155', fontSize: '0.95rem' }}>Últimos pagamentos desta conta</h3>
              {loadingHistorico ? (
                <div style={{ padding: '18px', textAlign: 'center', color: '#64748b', background: '#f8fafc', borderRadius: '11px' }}>Carregando histórico...</div>
              ) : historicoPagamentos.length === 0 ? (
                <div style={{ padding: '18px', textAlign: 'center', color: '#64748b', background: '#f8fafc', borderRadius: '11px' }}>Nenhum pagamento anterior registrado.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {historicoPagamentos.map(pagamento => (
                    <div key={pagamento._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '14px', padding: '11px 12px', border: '1px solid #e2e8f0', borderRadius: '11px', background: '#fff' }}>
                      <div>
                        <div style={{ color: '#334155', fontSize: '0.83rem', fontWeight: 700 }}>Vencimento {formatarDataLocal(pagamento.data_vencimento)}</div>
                        <div style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '3px' }}>
                          Pago em {new Date(pagamento.data_pagamento).toLocaleString('pt-BR')} · {pagamento.pago_por || 'Usuário'}
                        </div>
                        <div style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '3px' }}>Valor previsto: {fmt(pagamento.valor_previsto)}</div>
                      </div>
                      <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <div style={{ color: '#64748b', fontSize: '0.7rem', fontWeight: 700 }}>VALOR PAGO</div>
                        <div style={{ color: '#15803d', fontWeight: 800, marginTop: '2px' }}>{fmt(pagamento.valor_pago)}</div>
                        {Math.abs(Number(pagamento.valor_pago) - Number(pagamento.valor_previsto)) > 0.009 && (
                          <div style={{ color: Number(pagamento.valor_pago) > Number(pagamento.valor_previsto) ? '#b45309' : '#2563eb', fontSize: '0.7rem', fontWeight: 750, marginTop: '3px' }}>
                            {Number(pagamento.valor_pago) > Number(pagamento.valor_previsto) ? 'Com juros' : 'Com desconto'}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
